import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminLessonService, adminCursoService } from '../../shared/services';
import { 
  PlayCircle, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Filter,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function InstructorLessonsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState<string>('');
  const [ativoFilter, setAtivoFilter] = useState<string>('');

  // Buscar cursos para o filtro
  const { data: cursosData } = useQuery({
    queryKey: ['admin-cursos-list'],
    queryFn: () => adminCursoService.getCursos({}),
  });

  // Buscar lessons
  const { data: lessons, isLoading } = useQuery({
    queryKey: ['admin-lessons', courseFilter],
    queryFn: () => adminLessonService.getLessons({ 
      courseId: courseFilter || undefined,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (lessonId: string) => adminLessonService.deleteLesson(lessonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lessons'] });
    },
  });

  const handleDelete = (lessonId: string, lessonTitle: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a aula "${lessonTitle}"?`)) {
      deleteMutation.mutate(lessonId);
    }
  };

  // Filtrar lessons localmente por busca e status ativo
  const filteredLessons = lessons?.filter((lesson: any) => {
    const matchSearch = !search || 
      lesson.title?.toLowerCase().includes(search.toLowerCase()) ||
      lesson.description?.toLowerCase().includes(search.toLowerCase());
    
    const matchAtivo = ativoFilter === '' || 
      (ativoFilter === 'true' && lesson.isActive) ||
      (ativoFilter === 'false' && !lesson.isActive);
    
    return matchSearch && matchAtivo;
  });

  const getCursoNome = (courseId: string) => {
    const curso = (cursosData as any[])?.find((c: any) => c.id === courseId);
    return curso?.titulo || curso?.title || 'Curso não encontrado';
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Aulas</h1>
          <p className="text-gray-600 mt-2">
            Adicione, edite e organize as aulas dos cursos
          </p>
        </div>
        <Link
          to="/instructor/lessons/new"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Nova Aula
        </Link>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Aulas</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {lessons?.length || 0}
              </p>
            </div>
            <PlayCircle className="text-blue-600" size={40} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aulas Ativas</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {lessons?.filter((l: any) => l.isActive).length || 0}
              </p>
            </div>
            <CheckCircle className="text-green-600" size={40} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aulas Inativas</p>
              <p className="text-3xl font-bold text-gray-400 mt-1">
                {lessons?.filter((l: any) => !l.isActive).length || 0}
              </p>
            </div>
            <XCircle className="text-gray-400" size={40} />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar aulas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filtro por Curso */}
          <select
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos os cursos</option>
            {(cursosData as any[])?.map((curso: any) => (
              <option key={curso.id} value={curso.id}>
                {curso.titulo || curso.title}
              </option>
            ))}
          </select>

          {/* Filtro Status */}
          <select
            value={ativoFilter}
            onChange={(e) => setAtivoFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos os status</option>
            <option value="true">Apenas ativos</option>
            <option value="false">Apenas inativos</option>
          </select>
        </div>
      </div>

      {/* Tabela de Aulas */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-600">Carregando aulas...</div>
        ) : !filteredLessons || filteredLessons.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            Nenhuma aula encontrada.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ordem
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Curso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duração
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLessons.map((lesson: any) => (
                  <tr key={lesson.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        #{lesson.order}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <PlayCircle className="text-blue-600 flex-shrink-0" size={20} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {lesson.title}
                          </p>
                          {lesson.description && (
                            <p className="text-sm text-gray-500 truncate">
                              {lesson.description.substring(0, 60)}...
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {getCursoNome(lesson.courseId)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Clock size={16} />
                        {Math.floor(lesson.durationSeconds / 60)}min
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {lesson.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle size={14} />
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle size={14} />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/instructor/lessons/${lesson.id}/edit`}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={18} />
                        </Link>
                        <button
                          onClick={() => handleDelete(lesson.id, lesson.title)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
